import { countryNameByCountry } from '@escapenavigator/types/dist/constants/country-name-by-country';
import { PortalPartnerTypeEnum } from '@escapenavigator/types/dist/partners-portal/enum/portal-partner-type.enum';
import { PortalCityOptionRO } from '@escapenavigator/types/dist/partners-portal/portal-city-option.ro';
import {
    Anchor,
    Button,
    Card,
    Group,
    PasswordInput,
    Radio,
    Select,
    Stack,
    Stepper,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconBuilding, IconCheck, IconPencil } from '@tabler/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { t } from '../dictionary';

const RoleCard = ({
    icon,
    title,
    text,
    selected,
    onClick,
}: {
    icon: React.ReactNode;
    title: string;
    text: string;
    selected: boolean;
    onClick: () => void;
}) => (
    <UnstyledButton onClick={onClick} w='100%'>
        <Card
            padding='lg'
            style={{
                borderColor: selected ? 'var(--mantine-color-indigo-6)' : undefined,
                borderWidth: selected ? 2 : 1,
                background: selected ? 'var(--mantine-color-indigo-0)' : undefined,
            }}
        >
            <Group align='flex-start' wrap='nowrap'>
                <ThemeIcon
                    size={42}
                    radius='md'
                    variant={selected ? 'filled' : 'light'}
                    color='indigo'
                >
                    {icon}
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                    <Group justify='space-between'>
                        <Text fw={650}>{title}</Text>
                        <Radio checked={selected} onChange={onClick} />
                    </Group>
                    <Text size='sm' c='dimmed' mt={4}>
                        {text}
                    </Text>
                </div>
            </Group>
        </Card>
    </UnstyledButton>
);

export const RegisterPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [type, setType] = useState<PortalPartnerTypeEnum | null>(null);
    const [cities, setCities] = useState<PortalCityOptionRO[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.cities()
            .then(({ data }) => setCities(data))
            .catch(() => {});
    }, []);

    const cityOptions = useMemo(() => {
        const byCountry = new Map<
            string,
            { group: string; items: { value: string; label: string }[] }
        >();

        for (const city of cities) {
            const country = countryNameByCountry[city.country] ?? city.country.toUpperCase();

            if (!byCountry.has(country)) byCountry.set(country, { group: country, items: [] });

            byCountry.get(country).items.push({ value: String(city.id), label: city.title });
        }

        return [...byCountry.values()].sort((a, b) => a.group.localeCompare(b.group));
    }, [cities]);

    const isVenue = type === PortalPartnerTypeEnum.VENUE;

    const form = useForm({
        initialValues: {
            name: '',
            email: '',
            password: '',
            companyName: '',
            cityId: null as string | null,
        },
        validate: {
            name: (v) => (v.trim() ? null : 'Enter your name'),
            email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email'),
            password: (v) => (v.length >= 8 ? null : t.auth.passwordHint),
            companyName: (v) => (isVenue && !v.trim() ? 'Enter your venue name' : null),
            cityId: (v) => (isVenue && !v ? 'Choose a city' : null),
        },
    });

    const submit = async (values: typeof form.values) => {
        setLoading(true);

        try {
            await api.register({
                type,
                name: values.name,
                email: values.email,
                password: values.password,
                companyName: isVenue ? values.companyName : undefined,
                cityId: isVenue && values.cityId ? +values.cityId : undefined,
            });

            navigate(`/verify?email=${encodeURIComponent(values.email)}`);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card padding='xl' shadow='sm'>
            <Stack gap='lg'>
                <div>
                    <Title order={2}>{t.auth.registerTitle}</Title>
                    <Text c='dimmed' size='sm' mt={4}>
                        {t.auth.registerSubtitle}
                    </Text>
                </div>

                <Stepper active={step} size='sm' iconSize={28}>
                    <Stepper.Step label={t.auth.roleStep} icon={<IconCheck size={16} />}>
                        <Stack gap='sm' mt='md'>
                            <RoleCard
                                icon={<IconPencil size={22} />}
                                title={t.auth.authorCard}
                                text={t.auth.authorCardText}
                                selected={type === PortalPartnerTypeEnum.AUTHOR}
                                onClick={() => setType(PortalPartnerTypeEnum.AUTHOR)}
                            />
                            <RoleCard
                                icon={<IconBuilding size={22} />}
                                title={t.auth.venueCard}
                                text={t.auth.venueCardText}
                                selected={type === PortalPartnerTypeEnum.VENUE}
                                onClick={() => setType(PortalPartnerTypeEnum.VENUE)}
                            />

                            <Group justify='flex-end' mt='xs'>
                                <Button disabled={!type} onClick={() => setStep(1)}>
                                    {t.common.continue}
                                </Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label={t.auth.detailsStep} icon={<IconPencil size={16} />}>
                        <form onSubmit={form.onSubmit(submit)}>
                            <Stack gap='md' mt='md'>
                                <TextInput
                                    label={t.auth.fullName}
                                    placeholder='Jane Doe'
                                    {...form.getInputProps('name')}
                                />

                                {isVenue && (
                                    <TextInput
                                        label={t.auth.companyName}
                                        placeholder={t.auth.companyNamePlaceholder}
                                        {...form.getInputProps('companyName')}
                                    />
                                )}

                                {isVenue && (
                                    <Select
                                        label={t.auth.city}
                                        placeholder={t.auth.cityPlaceholder}
                                        data={cityOptions}
                                        searchable
                                        {...form.getInputProps('cityId')}
                                    />
                                )}

                                <TextInput
                                    label={t.auth.email}
                                    placeholder='you@example.com'
                                    autoComplete='email'
                                    {...form.getInputProps('email')}
                                />

                                <PasswordInput
                                    label={t.auth.password}
                                    description={t.auth.passwordHint}
                                    autoComplete='new-password'
                                    {...form.getInputProps('password')}
                                />

                                <Text size='xs' c='dimmed'>
                                    {t.auth.terms}
                                </Text>

                                <Group justify='space-between'>
                                    <Button variant='subtle' onClick={() => setStep(0)}>
                                        {t.common.back}
                                    </Button>
                                    <Button type='submit' loading={loading}>
                                        {t.auth.createAccountButton}
                                    </Button>
                                </Group>
                            </Stack>
                        </form>
                    </Stepper.Step>
                </Stepper>

                <Text size='sm' c='dimmed' ta='center'>
                    {t.auth.haveAccount}{' '}
                    <Anchor component={Link} to='/login' fw={600}>
                        {t.auth.login}
                    </Anchor>
                </Text>
            </Stack>
        </Card>
    );
};
